import React, { useState, FC } from "react";
import { read, utils, writeFileXLSX } from "xlsx";
import { Upload, Button, message, notification, Flex, theme } from "antd";
import { ExcelIcon } from "@/components/icon/ExcelIcon";
import { axiosInstance } from "@/utilities";
import { API_URL_LINKED_IMPORT } from "@/services/XCONS_ENV";

interface UpdateButtonProps {
  formOk: boolean;
  translate: any;
  configXlsData: any;
  sellerCode: string;
  refetchTableXls: any;
  enableProducts?: boolean;
  /** Deshabilitado desde afuera: mientras se procesa una importación no se sube otra. */
  disabled?: boolean;
  /**
   * Al empezar a subir, con el nombre con que se guarda: el panel muestra la fila en proceso y
   * lleva la tabla a la vista (xcons-docs#609). Un Excel chico se procesa dentro del mismo
   * pedido, así que sin esto nunca se vería "en proceso".
   */
  onEmpezo?: (nombre: string) => void;
  /** Al terminar la subida, con si salió bien. */
  onTermino?: (ok: boolean) => void;
}

export const UpdateButton: FC<UpdateButtonProps> = React.memo(
  ({
    formOk = false,
    translate: t,
    configXlsData: configHeaders,
    sellerCode,
    refetchTableXls,
    enableProducts,
    disabled = false,
    onEmpezo,
    onTermino,
  }) => {
    const { token } = theme.useToken();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [uploadKey, setUploadKey] = useState<number>(0);

    const toInt = (v: any, d = 0) => {
      const n = Number(String(v ?? "").trim());
      return Number.isFinite(n) ? Math.trunc(n) : d;
    };

    const toOptInt = (v: any): number | undefined => {
      if (v === undefined || v === null || String(v).trim() === "")
        return undefined;
      const n = Number(String(v).trim());
      return Number.isFinite(n) ? Math.trunc(n) : undefined;
    };

    const toOptStr = (v: any) =>
      v === undefined || v === null || String(v).trim() === "" ? undefined : v;

    const toOptVal = (v: any) => {
      if (v === undefined || v === null || String(v).trim() === "")
        return undefined;
      const n = Number(v);
      if (Number.isFinite(n) && n === 0) return undefined;
      return v;
    };

    const handleUpload = async (options: any) => {
      setIsLoading(true);
      const { file, onError, onSuccess } = options;
      const reader = new FileReader();
      const { firstLine, dateFormat } = configHeaders?.data;

      const currentDate = new Date();
      const formattedDate = `${currentDate.getFullYear()}-${(
        currentDate.getMonth() + 1
      )
        .toString()
        .padStart(2, "0")}-${currentDate
        .getDate()
        .toString()
        .padStart(2, "0")}`;
      const formattedTime = `${currentDate
        .getHours()
        .toString()
        .padStart(2, "0")}-${currentDate
        .getMinutes()
        .toString()
        .padStart(2, "0")}-${currentDate
        .getSeconds()
        .toString()
        .padStart(2, "0")}`;

      const newFileName = `${file.name
        .split(".")
        .slice(0, -1)
        .join(".")}_${formattedDate} ${formattedTime}.${file.name
        .split(".")
        .pop()}`;

      reader.onload = (event) => {
        try {
          const binaryStr = event.target?.result as ArrayBuffer;
          const bytes = new Uint8Array(binaryStr);
          let binary = "";
          for (let i = 0; i < bytes.byteLength; i += 8192) {
            binary += String.fromCharCode.apply(
              null,
              bytes.subarray(i, i + 8192) as unknown as number[],
            );
          }
          const fileBase64 = btoa(binary);
          const workbook = read(binaryStr, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const headers: any = (
            utils.sheet_to_json(worksheet, {
              header: firstLine,
            })[0] as string[]
          ).map((h) => (typeof h === "string" ? h.trim() : h));
          const configHeadersArray = Object.values(configHeaders.data).filter(
            (h) => typeof h === "string" && h.trim() !== "",
          );
          const matchedHeaders = headers.filter((header: string) =>
            configHeadersArray.includes(header),
          );

          const manufacturerSkuColumnInExcel =
            configHeaders.data.manufacturerSku &&
            headers.includes(configHeaders.data.manufacturerSku);

          const enabledColumnInExcel =
            configHeaders.data.enabled &&
            headers.includes(configHeaders.data.enabled);

          // Columnas personalizadas: sólo las que existen como header en el Excel.
          const customColumns: string[] = (
            Array.isArray(configHeaders.data.customColumns)
              ? configHeaders.data.customColumns
              : []
          ).filter((col: string) => headers.includes(col));

          if (matchedHeaders.length > 0) {
            let numericSkuCount = 0;
            let numericMfgSkuCount = 0;
            const jsonData: any = utils
              .sheet_to_json(worksheet, {
                header: headers,
                range: firstLine, // ignore header row
              })
              .map((row: any) => {
                const rawSku = row[configHeaders.data.sku];
                const rawMfgSku = row[configHeaders.data.manufacturerSku];
                if (typeof rawSku === "number") numericSkuCount++;
                if (
                  manufacturerSkuColumnInExcel &&
                  typeof rawMfgSku === "number"
                ) {
                  numericMfgSkuCount++;
                }
                return {
                  sku: String(rawSku ?? "").trim(),
                  name: toOptStr(row[configHeaders.data.name]),
                  manufacturer: toOptStr(row[configHeaders.data.brand]),
                  // Pass raw values — backend normalises "1.234,56" / "123,45" / numbers
                  price: toOptVal(row[configHeaders.data.price]),
                  costPrice: toOptVal(row[configHeaders.data.costPrice]),
                  taxes: row[configHeaders.data.taxes] || false,
                  specialPrice: toOptStr(row[configHeaders.data.specialPrice]),
                  // Both field names accepted by the backend validator
                  specialPriceFrom: toOptStr(
                    row[configHeaders.data.specialPriceFrom],
                  ),
                  specialPriceTo: toOptStr(
                    row[configHeaders.data.specialPriceTo],
                  ),
                  stock: toOptInt(row[configHeaders.data.stock]),
                  leadTime: toOptInt(row[configHeaders.data.leadTime]),
                  featured: toOptStr(row[configHeaders.data.featured]),
                  manufacturerSku: manufacturerSkuColumnInExcel
                    ? String(rawMfgSku ?? "").trim()
                    : undefined,
                  enabled: enabledColumnInExcel
                    ? row[configHeaders.data.enabled]
                    : undefined,
                  // { columna: valor } por fila — el back lo persiste y lo
                  // propaga al producto y al catalog-pricing (additionalData.customFields).
                  customFields: customColumns.reduce(
                    (acc: Record<string, string>, col: string) => {
                      const v = row[col];
                      if (
                        v !== undefined &&
                        v !== null &&
                        String(v).trim() !== ""
                      ) {
                        acc[col] = String(v).trim();
                      }
                      return acc;
                    },
                    {} as Record<string, string>,
                  ),
                };
              });

            if (numericSkuCount > 0) {
              notification.warning({
                message: "SKU con formato numérico",
                description: `${numericSkuCount} fila${
                  numericSkuCount !== 1 ? "s tienen" : " tiene"
                } el SKU como número en el Excel. Si alguno debería tener cero inicial (ej: "09751"), puede haberse perdido y se creará como producto nuevo en lugar de actualizar el existente.`,
                duration: 0,
              });
            }

            if (numericMfgSkuCount > 0) {
              notification.warning({
                message: "Sku Fabricante con formato numérico",
                description: `${numericMfgSkuCount} fila${
                  numericMfgSkuCount !== 1 ? "s tienen" : " tiene"
                } el Sku Fabricante como número en el Excel. Si alguno debería tener cero inicial (ej: "0303533"), puede haberse perdido. El back rechazará las filas que no encuentre en catálogo.`,
                duration: 0,
              });
            }

            if (jsonData) {
              message.success(
                t("message.fileUploadProcesSuccess", { fileName: file.name }),
              );
              setUploadKey((k) => k + 1);
              uploadToAPI(newFileName, jsonData, fileBase64);
            } else {
              message.error(t("message.fileUploadError"));
              setUploadKey((k) => k + 1);
              setIsLoading(false);
            }
          } else {
            message.error(t("message.fileUploadError"));
            setUploadKey((k) => k + 1);
            setIsLoading(false);
          }
        } catch (error) {
          message.error(t("message.fileUploadErrorProcessing"));
          setUploadKey((k) => k + 1);
          setIsLoading(false);
          onError(null, file);
        }
      };

      reader.readAsArrayBuffer(file);
    };

    const uploadToAPI = async (
      name: string,
      data: any,
      fileBase64?: string,
    ) => {
      const payload: Record<string, any> = {
        name,
        items: data,
        fileBase64,
      };
      if (enableProducts !== undefined) {
        payload.enableProducts = enableProducts;
      }
      onEmpezo?.(name);
      let ok = false;
      try {
        const response = await axiosInstance.post(
          `${API_URL_LINKED_IMPORT}/${sellerCode}`,
          payload,
        );
        const errorCount: number = response?.data?.validationErrors ?? 0;
        if (errorCount > 0) {
          notification.warning({
            message: t("message.documentUpdated"),
            description: `${errorCount} fila${
              errorCount !== 1 ? "s" : ""
            } con errores de validación fueron ignoradas. Revisá el detalle en el historial.`,
          });
        } else {
          notification.success({ message: t("message.documentUpdated") });
        }
        await refetchTableXls();
        ok = true;
      } catch (error: any) {
        notification.error({
          message: t("message.documentUpdatedError"),
          description: error.message,
        });
      } finally {
        setIsLoading(false);
        onTermino?.(ok);
      }
    };

    const handleBeforeUpload = (file: any) => {
      const limitMB = 15;
      const isLessThanLimit = file.size / 1024 / 1024 < limitMB;
      if (!isLessThanLimit) {
        message.error(
          `El archivo "${file.name}" excede el tamaño máximo permitido de ${limitMB}MB`,
        );
      }
      return isLessThanLimit ? true : Upload.LIST_IGNORE;
    };

    return (
      <Flex vertical>
        <Upload
          key={uploadKey}
          customRequest={handleUpload}
          beforeUpload={handleBeforeUpload}
          maxCount={1}
          accept=".xls, .xlsx"
          showUploadList={false}
        >
          <Button
            disabled={!formOk || disabled}
            type="primary"
            size="middle"
            icon={
              <ExcelIcon
                style={
                  {
                    fontSize: 20,
                    // La banda toma el blanco del botón: las letras, su fondo.
                    "--excel-icon-letras":
                      formOk && !disabled
                        ? token.colorPrimary
                        : token.colorBgContainerDisabled,
                  } as React.CSSProperties
                }
              />
            }
            iconPosition="end"
            loading={isLoading}
          >
            {isLoading ? t("buttons.uploading") : "Cargar XLS"}
          </Button>
        </Upload>
      </Flex>
    );
  },
);
